import {
  DELETEResponse,
  GETAccountNumbers,
  GETAccountResponse,
  GETAccountsResponse,
  GETAddressFromPostalCodeResponse,
  IAccountCommunicationChannelConfig,
  IAccountCommunicationChannelResponse,
  IUUIDResponse
} from '@cbgms/api'
import * as requests from '@cbgms/api/modules'
import { AccountNumberType } from '@cbgms/api/modules/enums'
import { IPaginatedRequest } from '@cbgms/api/request'
import { AsyncAction } from '@cbgms/base/redux/async-action'
import { createAsyncApiAction } from '@cbgms/base/redux/create-async-action'
import * as types from './types'
import {
  postVehicleLessor,
  sendOpenInvoicesStatement as sendOpenInvoicesStatementRequest,
  updateAccountForLeaseFlow
} from '@cbgms/api/modules/accounts/requests'

export const createAccount = (data: any) =>
  AsyncAction<IUUIDResponse>({
    type: [types.CREATE_ACCOUNT, types.CREATE_ACCOUNT_SUCCESS, types.CREATE_ACCOUNT_FAILED],
    payload: {
      api: 'gms',
      request: {
        method: 'POST',
        url: '/modules/accounts',
        data
      }
    }
  })

export const updateAccount = (accountUUID: string, data: any) =>
  AsyncAction<IUUIDResponse>({
    type: [types.UPDATE_ACCOUNT, types.UPDATE_ACCOUNT_SUCCESS, types.UPDATE_ACCOUNT_FAILED],
    payload: {
      api: 'gms',
      request: {
        method: 'PUT',
        url: `/modules/accounts/${accountUUID}`,
        data
      }
    }
  })

export const updateAccountNumber = (accountUUID: string, numberType: AccountNumberType, data: any) => ({
  type: [types.UPDATE_ACCOUNT_NUMBER, types.UPDATE_ACCOUNT_NUMBER_SUCCESS, types.UPDATE_ACCOUNT_NUMBER_FAILED],
  payload: {
    api: 'gms',
    request: {
      method: 'PUT',
      url: `/modules/accounts/${accountUUID}/numbers/${numberType}`,
      data
    }
  }
})

export const createAccountNumber = (uuid: string, data: any) => ({
  type: [types.CREATE_ACCOUNT_NUMBER, types.CREATE_ACCOUNT_NUMBER_SUCCESS, types.CREATE_ACCOUNT_NUMBER_FAILED],
  payload: {
    api: 'gms',
    request: {
      method: 'POST',
      url: `/modules/accounts/${uuid}/numbers`,
      data
    }
  }
})

export const deleteAccountNumber = (accountUUID: string, numberType: AccountNumberType) => ({
  type: [types.DELETE_ACCOUNT_NUMBER, types.DELETE_ACCOUNT_NUMBER_SUCCESS, types.DELETE_ACCOUNT_NUMBER_FAILED],
  payload: {
    api: 'gms',
    request: {
      method: 'DELETE',
      url: `/modules/accounts/${accountUUID}/numbers/${numberType}`
    }
  }
})

export const updateSageCashAccountNumber = (accountUUID: string, data: any) => ({
  type: [types.UPDATE_SAGE_ACCOUNT_NUMBER, types.UPDATE_SAGE_ACCOUNT_NUMBER_SUCCESS, types.UPDATE_SAGE_ACCOUNT_NUMBER_FAILED],
  payload: {
    api: 'gms',
    request: {
      method: 'PUT',
      url: `/modules/accounts/${accountUUID}/sageaccountnumber`,
      data
    }
  }
})

export interface LoadAccountSuccess {
  type: typeof types.LOAD_ACCOUNT_SUCCESS
  payload: {
    data: GETAccountResponse
  }
}

export const loadAccount = (accountUUID: string) =>
  AsyncAction<GETAccountResponse>({
    type: [types.LOAD_ACCOUNT, types.LOAD_ACCOUNT_SUCCESS, types.LOAD_ACCOUNT_FAILED],
    payload: {
      api: 'gms',
      request: {
        method: 'GET',
        url: `/modules/accounts/${accountUUID}`
      }
    }
  })

export const deleteAccount = (accountUUID: string, dryRun: boolean, reason: string) =>
  AsyncAction<DELETEResponse>({
    type: [types.DELETE_ACCOUNT, types.DELETE_ACCOUNT_SUCCESS, types.DELETE_ACCOUNT_FAILED],
    payload: {
      api: 'gms',
      request: {
        method: 'DELETE',
        url: `/modules/accounts/${accountUUID}`,
        params: {
          DryRun: dryRun,
          Reason: reason
        }
      }
    }
  })

export interface LoadAccountNumbersSuccess {
  type: typeof types.LOAD_ACCOUNT_NUMBERS_SUCCESS
  payload: {
    data: GETAccountNumbers
  }
}

export const loadAccountNumbers = (accountUUID: string) =>
  AsyncAction<GETAccountNumbers>({
    type: [types.LOAD_ACCOUNT_NUMBERS, types.LOAD_ACCOUNT_NUMBERS_SUCCESS, types.LOAD_ACCOUNT_NUMBERS_FAILED],
    payload: {
      api: 'gms',
      request: {
        method: 'GET',
        url: `/modules/accounts/${accountUUID}/numbers`
      }
    }
  })

export const loadAccounts = (params: IPaginatedRequest) =>
  AsyncAction<GETAccountsResponse>({
    type: [types.LOAD_ACCOUNTS, types.LOAD_ACCOUNTS_SUCCESS, types.LOAD_ACCOUNTS_FAILED],
    payload: {
      api: 'gms',
      request: {
        params: {
          ...params
        },
        method: 'GET',
        url: '/modules/accounts'
      }
    }
  })

export const loadPartSupplierAccounts = (params: IPaginatedRequest) =>
  createAsyncApiAction<GETAccountsResponse>('LOAD_PART_SUPPLIER_ACCOUNTS', 'gms', {
    params,
    method: 'GET',
    url: '/modules/accounts/part-suppliers'
  })

export interface ResetAccount {
  type: typeof types.RESET_ACCOUNT
}

export const resetAccount = (): ResetAccount => ({
  type: types.RESET_ACCOUNT
})

export const getAddressFromPostalCode = (CountryCode: string, HouseNumber: string, PostalCode: string) =>
  AsyncAction<GETAddressFromPostalCodeResponse>({
    type: [types.GET_ADDRESS_FROM_POSTAL_CODE, types.GET_ADDRESS_FROM_POSTAL_CODE_SUCCESS, types.GET_ADDRESS_FROM_POSTAL_CODE_FAILED],
    payload: requests.getAddressFromPostalCodeRequest(CountryCode, HouseNumber, PostalCode)
  })

export const getCommunicationChannelsNewAccountConfig = () =>
  AsyncAction<IAccountCommunicationChannelResponse>({
    type: [
      types.GET_COMMUNICATION_CHANNEL_DEFAULT_CONFIG,
      types.GET_COMMUNICATION_CHANNEL_DEFAULT_CONFIG_SUCCESS,
      types.GET_COMMUNICATION_CHANNEL_DEFAULT_CONFIG_FAILED
    ],
    payload: {
      api: 'gms',
      request: requests.getCommunicationChannelsDefaultAccountConfig()
    }
  })

export const getCommunicationChannelsAccountConfig = (accountUUID: string) =>
  AsyncAction<IAccountCommunicationChannelResponse>({
    type: [
      types.GET_COMMUNICATION_CHANNEL_ACCOUNT_CONFIG,
      types.GET_COMMUNICATION_CHANNEL_ACCOUNT_CONFIG_SUCCESS,
      types.GET_COMMUNICATION_CHANNEL_ACCOUNT_CONFIG_FAILED
    ],
    payload: {
      api: 'gms',
      request: requests.getCommunicationChannelsAccountConfig(accountUUID)
    }
  })

export const putCommunicationChannelsAccountConfig = (accountUUID: string, config: IAccountCommunicationChannelConfig) =>
  AsyncAction<IAccountCommunicationChannelResponse>({
    type: [
      types.GET_COMMUNICATION_CHANNEL_ACCOUNT_CONFIG,
      types.GET_COMMUNICATION_CHANNEL_ACCOUNT_CONFIG_SUCCESS,
      types.GET_COMMUNICATION_CHANNEL_ACCOUNT_CONFIG_FAILED
    ],
    payload: {
      api: 'gms',
      request: requests.putCommunicationChannelsAccountConfig(accountUUID, config)
    }
  })

export const createForVehicleLessor = (VehicleUUID: string, LeaseCompanyUserUUID?: string) =>
  createAsyncApiAction<IUUIDResponse>('gms', postVehicleLessor(VehicleUUID, LeaseCompanyUserUUID))

export const putAccountToLeaseFlow = (params: { registration: string; leaseCompanyUserID?: string }) =>
  createAsyncApiAction<IUUIDResponse>('gms', updateAccountForLeaseFlow(params))

export const sendOpenInvoicesStatement = (accountUUID: string, data: { InvoiceUUIDs: string[] }) =>
  createAsyncApiAction<{ data: string }>('gms', sendOpenInvoicesStatementRequest(accountUUID, data))
